import { Button, Form, FormInstance } from 'antd';
import { NamePath } from 'antd/es/form/interface';
import React, { useEffect, useState } from 'react';

type SubmitFormButtonProps = {
  form: FormInstance;
  text: string;
  icon?: React.ReactNode;
  /** Fields with async validators (e.g. DNI). Not re-validated on each keystroke. */
  skipLiveValidation?: NamePath[];
};

const EMPTY_SKIP: NamePath[] = [];

const fieldKey = (name: NamePath): string => (Array.isArray(name) ? name : [name]).join('.');

const isEmptyValue = (value: unknown): boolean => value === undefined || value === null || value === '';

const SubmitFormButton: React.FC<SubmitFormButtonProps> = ({ form, text, icon, skipLiveValidation = EMPTY_SKIP }) => {
  const [submittable, setSubmittable] = useState(false);
  const values = Form.useWatch([], form);

  useEffect(() => {
    let cancelled = false;

    const skippedEmpty = skipLiveValidation.some((name) => isEmptyValue(form.getFieldValue(name)));
    if (skippedEmpty) {
      setSubmittable(false);
      return;
    }

    const namesToValidate = form
      .getFieldsError()
      .map((field) => field.name)
      .filter((name) => !skipLiveValidation.some((skip) => fieldKey(skip) === fieldKey(name)));

    if (namesToValidate.length === 0) {
      setSubmittable(true);
      return;
    }

    form.validateFields(namesToValidate, { validateOnly: true }).then(
      () => {
        if (!cancelled) setSubmittable(true);
      },
      () => {
        if (!cancelled) setSubmittable(false);
      },
    );

    return () => {
      cancelled = true;
    };
  }, [form, values, skipLiveValidation]);

  return (
    <Form.Item shouldUpdate noStyle>
      {() => {
        const skippedHasErrors = skipLiveValidation.some((name) => form.getFieldError(name).length > 0);
        return (
          <Button type='primary' htmlType='submit' disabled={!submittable || skippedHasErrors}>
            {icon}
            {text}
          </Button>
        );
      }}
    </Form.Item>
  );
};

export default SubmitFormButton;
